import { colors, sizing, noop } from '@chillibean/component-library';
import { clsx } from 'clsx';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Loader, LoaderVariant } from '../../../../components/presentational';
import { icons as customIcons } from '../../../../utils';

const CELL_CONSTANTS = {
  cellWrapper: {
    height: 56,
    width: 96,
  },
};

export function TableDataCell(props) {
  const {
    active = false,
    cellData,
    disabled = false,
    filled = false,
    loading = false,
    onAppend = noop,
    onRemove = noop,
  } = props;

  // VARIABLES
  const notActive = !active && !filled;

  // HOOKS
  // React
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (clicked) {
      setTimeout(() => setClicked(false), 2000);
    }
  }, [clicked]);

  const determinIconFilled = ({ check, round }) => {
    let icon;
    if (filled) {
      icon = customIcons.checkWithCircleIcon(check);
    } else {
      icon = customIcons.roundIcon(round);
    }
    return icon;
  };

  const determineIcon = () => {
    let icon;
    if (disabled && !notActive) {
      icon = determinIconFilled({
        check: { fill: colors.blue.primaryAlpha('75'), height: 20, viewBox: '0 0 20 20', width: 20 },
        round: { fill: colors.grey.primaryAlpha('75'), height: 20, viewBox: '0 0 20 20', width: 20 },
      });
    } else if (active) {
      icon = determinIconFilled({
        check: { fill: colors.blue.primary, height: 20, viewBox: '0 0 20 20', width: 20 },
        round: { fill: colors.grey.primary, height: 20, viewBox: '0 0 20 20', width: 20 },
      });
    } else if (notActive) {
      icon = customIcons.roundIcon({
        fill: colors.grey.primaryAlpha('20'),
        height: 20,
        viewBox: '0 0 20 20',
        width: 20,
      });
    }
    return icon;
  };

  const detemineHoverIcon = () => {
    let icon;
    if (filled) {
      icon = clicked ? customIcons.animatedCheckCircleIcon() : customIcons.crossIcon();
    } else {
      icon = clicked ? customIcons.animatedRoundIcon() : customIcons.plusIcon({ opacity: 0.2 });
    }
    return icon;
  };

  const handleIconClick = () => {
    if (!notActive && !disabled) {
      if (filled) {
        setClicked(true);
        onRemove(cellData);
      } else {
        setClicked(true);
        onAppend(cellData);
      }
    }
  };

  if (loading) {
    return <Loader variant={LoaderVariant.CELL} />;
  }

  return (
    <TableCellWrapper>
      <CellWrapper
        className={clsx({
          'active': active,
          'disabled': disabled && !notActive,
          'not-active': notActive,
        })}
        onClick={handleIconClick}
      >
        <IconWrapper>
          <MainIcon>{determineIcon()}</MainIcon>
          <HoverIcon>{detemineHoverIcon()}</HoverIcon>
        </IconWrapper>
      </CellWrapper>
    </TableCellWrapper>
  );
}

const TableCellWrapper = styled.div`
  padding: 7px;
`;

const IconWrapper = styled.div``;

const HoverIcon = styled.div`
  opacity: 0;
  max-height: 0;
`;

const MainIcon = styled.div`
  opacity: 1;
  max-height: ${sizing.slarge};
`;

const CellWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  height: ${CELL_CONSTANTS.cellWrapper.height}px;
  width: ${CELL_CONSTANTS.cellWrapper.width}px;

  border-radius: ${sizing.xsmall};
  background-color: ${colors.grey.secondaryAlpha('40')};
  cursor: pointer;

  &.active {
    background-color: ${colors.grey.secondary};
  }

  &.not-active {
    cursor: not-allowed;
  }

  &.disabled {
    background-color: ${colors.grey.secondary};
  }

  &:not(.not-active):not(.disabled):hover {
    ${MainIcon} {
      transition: opacity 0.2s ease-in-out;
      opacity: 0;
      max-height: 0;
    }

    ${HoverIcon} {
      transition: opacity 0.2s ease-in-out;
      opacity: 1;
      max-height: ${sizing.slarge};
    }
  }
`;

TableDataCell.propTypes = {
  active: PropTypes.bool,
  cellData: PropTypes.shape({
    studyEventDefinitionId: PropTypes.number,
    studyProcedureDefinitionId: PropTypes.number,
  }).isRequired,
  disabled: PropTypes.bool,
  filled: PropTypes.bool,
  loading: PropTypes.bool,
  onAppend: PropTypes.func,
  onRemove: PropTypes.func,
};

export default TableDataCell;
